import { fetchApi } from '@/services/api-client';

import type { StandingEntry } from '../celebrations/celebrations';

export const SAND_DOLLAR_STATS_PATH = '/sand-dollar-stats';

export type MonthlyCount = {
  month: string;
  count: number;
};

export type CategoryCount = {
  categoryId: string;
  categoryName: string | null;
  categoryColor: string | null;
  count: number;
};

export type TeacherCount = {
  teacherUserId: string | null;
  teacherEmail: string | null;
  teacherName: string | null;
  autoRule: string | null;
  count: number;
};

/** How often a student is recognised, over the school year so far. */
export type StudentRates = {
  awards: number;
  schoolDays: number;
  perSchoolDay: number;
  perSchoolWeek: number;
  /** Per twenty school days — roughly a month of them. */
  perSchoolMonth: number;
  gradeAveragePerSchoolDay: number;
  /** How far above or below the grade average, in percent; null when the grade has no awards. */
  vsGradePct: number | null;
  lastAwardedAt: string | null;
  schoolDaysSinceLastAward: number | null;
};

export type StudentStats = {
  studentId: string;
  total: number;
  byMonth: MonthlyCount[];
  byCategory: CategoryCount[];
  byTeacher: TeacherCount[];
  rates: StudentRates;
};

/** ISO weekday, 1 = Monday .. 7 = Sunday. */
export type WeekdayCount = {
  weekday: number;
  count: number;
};

export type DailyCount = {
  date: string;
  count: number;
};

export type OverviewGradeRow = {
  gradeLevel: number;
  students: number;
  awards: number;
  studentsAwarded: number;
  coveragePct: number;
  perStudentPerSchoolDay: number;
  perStudentPerSchoolWeek: number;
};

export type SpreadBucketKey = '0' | '1' | '2-3' | '4-5' | '6-10' | '11+';

export type SpreadBucket = {
  bucket: SpreadBucketKey;
  students: number;
};

export type StaffAwarding = {
  teacherUserId: string | null;
  teacherName: string | null;
  autoRule: string | null;
  photoUrl: string | null;
  awards: number;
  distinctStudents: number;
  perSchoolDay: number;
};

export type StaffSilent = {
  teacherUserId: string;
  teacherName: string | null;
};

export type OverviewTotals = {
  awards: number;
  perSchoolDay: number;
  perStudentPerSchoolDay: number;
  studentsTotal: number;
  studentsAwarded: number;
  coveragePct: number;
  medianPerStudent: number;
  staffTotal: number;
  staffAwarding: number;
};

export type OverviewResponse = {
  window: { from: string; to: string; schoolDays: number };
  totals: OverviewTotals;
  byGrade: OverviewGradeRow[];
  byDay: DailyCount[];
  byWeekday: WeekdayCount[];
  byCategory: CategoryCount[];
  byStaff: StaffAwarding[];
  staffSilent: StaffSilent[];
  spread: SpreadBucket[];
};

export type TeacherStats = {
  teacherUserId: string;
  total: number;
  byMonth: MonthlyCount[];
  byCategory: CategoryCount[];
};

export type HouseTotal = {
  houseId: string;
  houseName: string | null;
  houseColor: string | null;
  fromStudents: number;
  fromAdjustments: number;
  total: number;
};

export type HouseMonthly = {
  houseId: string;
  houseName: string | null;
  month: string;
  count: number;
};

export type HouseStats = {
  totals: HouseTotal[];
  byMonth: HouseMonthly[];
};

export type LeaderboardEntry = StandingEntry;

export type LeaderboardBucket = {
  key: string;
  label: string;
  entries: LeaderboardEntry[];
};

export type LeaderboardResponse = {
  scope: 'overall' | 'house' | 'grade';
  period: 'week' | 'month' | 'year' | 'all';
  from: string | null;
  to: string | null;
  buckets: LeaderboardBucket[];
};

export type StatsWindow = { from?: string; to?: string };

export type ClassListRow = {
  studentId: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  gradeLevel: number | null;
  houseId: string | null;
  houseName: string | null;
  houseColor: string | null;
  /** Position inside their own grade, competition-ranked; null when the grade is unset. */
  rankInGrade: number | null;
  gradeSize: number | null;
  /** How many different staff members have recognised them in the window. */
  distinctAwarders: number;
  /** The value they are most often recognised for; null when they hold no awards. */
  /** The value they are most often recognised for, with the category's own swatch. */
  topCategory: { name: string; count: number; color: string | null } | null;
  /** The staff member who most often recognises them; null when they hold no awards. */
  topAwarder: { name: string; count: number } | null;
  rates: StudentRates;
};

export type ClassListSummary = {
  students: number;
  awards: number;
  /** Counts the un-awarded as the zeros they are; an average over only the awarded lies. */
  medianPerStudent: number;
  recognisedPct: number;
};

export type ClassListResponse = {
  window: { from: string; to: string; schoolDays: number };
  summary: ClassListSummary;
  students: ClassListRow[];
};

export const sandDollarStatsApi = {
  studentStats: (id: string) => fetchApi<StudentStats>(`${SAND_DOLLAR_STATS_PATH}/students/${id}`),
  teacherStats: (userId: string) => fetchApi<TeacherStats>(`${SAND_DOLLAR_STATS_PATH}/teachers/${userId}`),
  overview: (window: StatsWindow = {}) => {
    const params = new URLSearchParams();
    if (window.from) params.append('from', window.from);
    if (window.to) params.append('to', window.to);
    const qs = params.toString();
    return fetchApi<OverviewResponse>(`${SAND_DOLLAR_STATS_PATH}/overview${qs ? `?${qs}` : ''}`);
  },
  houseStats: (window: StatsWindow = {}) => {
    const params = new URLSearchParams();
    if (window.from) params.append('from', window.from);
    if (window.to) params.append('to', window.to);
    const qs = params.toString();
    return fetchApi<HouseStats>(`${SAND_DOLLAR_STATS_PATH}/houses${qs ? `?${qs}` : ''}`);
  },
  leaderboard: (
    period: 'week' | 'month' | 'year' | 'all' = 'month',
    scope: 'overall' | 'house' | 'grade' = 'overall',
    limit: number | 'all' = 10,
    window: StatsWindow = {}
  ) => {
    const params = new URLSearchParams({ period, scope, limit: String(limit) });
    if (window.from) params.append('from', window.from);
    if (window.to) params.append('to', window.to);
    return fetchApi<LeaderboardResponse>(`${SAND_DOLLAR_STATS_PATH}/leaderboard?${params}`);
  },
  classList: (window: StatsWindow = {}) => {
    const params = new URLSearchParams();
    if (window.from) params.append('from', window.from);
    if (window.to) params.append('to', window.to);
    const query = params.toString();
    return fetchApi<ClassListResponse>(`${SAND_DOLLAR_STATS_PATH}/students${query ? `?${query}` : ''}`);
  },
};
